/**
 * Dashboard — AI-first command center.
 *
 * Seven sections, top to bottom:
 *
 *   1. Logo mark + greeting (Syne 28px display headline)
 *   2. Command bar (full-width pill, rotating placeholder)
 *   3. Module shortcuts (6 icon circles)
 *   4. Morning Brief + Next Meeting (2-col)
 *   5. To-Do Today + Sentinel Alerts (2-col)
 *   6. Stats bar (4 metrics)
 *   7. Agent cards (Atlas / Sentinel / Pilot)
 *
 * The greeting + all section data come from
 * ``GET /dashboard/briefing`` — pure DB queries, no Claude calls,
 * 5-minute server cache. The command bar uses the autonomous
 * engine at ``POST /ai/command`` (built in the previous turn).
 *
 * Mobile: every grid drops to 1 column except the stats bar
 * which becomes 2x2. Command bar stays full width.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Bell,
  Bot,
  Briefcase,
  CalendarDays,
  CheckCircle2,
  FileSearch,
  Heart,
  ListChecks,
  Send,
  Shield,
  Sparkles,
  type LucideIcon,
  Users,
  Wallet,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import {
  getDashboardBriefing,
  type AgentSummary,
  type DashboardAlert,
  type DashboardBriefing,
  type DashboardTodo,
  type NextMeeting,
} from "@/api/dashboard";

import { CommandBar } from "@/components/command/CommandBar";
import { LowCreditBanner } from "@/components/shared/LowCreditBanner";

// ════════════════════════════════════════════════════════════════════
// Page
// ════════════════════════════════════════════════════════════════════

export function DashboardPage() {
  const { user } = useAuth();
  const [briefing, setBriefing] = useState<DashboardBriefing | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getDashboardBriefing()
      .then((b) => {
        if (!cancelled) setBriefing(b);
      })
      .catch((err) => {
        // Briefing is non-essential for non-admins; fail soft.
        console.warn("getDashboardBriefing failed", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <motion.div {...pageTransition} className="space-y-8 max-w-5xl mx-auto">
      <Header user={user} briefing={briefing} />

      {/* AI credit pressure — fires at ≥80% monthly usage. Banner
          enforces its own role gating (SUPER_ADMIN + HR_ADMIN only)
          and is dismissible per-cycle. Renders above the command
          bar so an admin sees it the moment they land on the page. */}
      <LowCreditBanner />

      {/* Section 2 — Command bar (the interaction surface) */}
      <CommandBar variant="inline" />

      {/* Section 3 — module shortcuts */}
      <ModuleShortcuts />

      {/* Section 4 — morning brief + next meeting */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MorningBriefCard briefing={briefing} loading={loading} />
        <NextMeetingCard meeting={briefing?.next_meeting ?? null} />
      </div>

      {/* Section 5 — todos + alerts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TodoCard todos={briefing?.todos ?? []} />
        <AlertsCard alerts={briefing?.alerts ?? []} />
      </div>

      {/* Section 6 — stats bar */}
      <StatsBar briefing={briefing} />

      {/* Section 7 — agent cards */}
      <AgentRow agents={briefing?.agents ?? {}} />
    </motion.div>
  );
}

export default DashboardPage;

// ════════════════════════════════════════════════════════════════════
// Section 1 — Logo + greeting
// ════════════════════════════════════════════════════════════════════

function Header({
  user,
  briefing,
}: {
  user: { first_name?: string | null; email?: string | null } | null;
  briefing: DashboardBriefing | null;
}) {
  // Greeting name resolution — chain of fallbacks so we never land
  // on the impersonal "there":
  //   1. user.first_name (from the linked employee record)
  //   2. email local part (e.g. "angela" from "angela@acme.com")
  //   3. "there" as final fallback
  const greetingName = (() => {
    const first = (user?.first_name || "").trim();
    if (first) return first;
    const email = (user?.email || "").trim();
    if (email && email.includes("@")) {
      const local = email.split("@", 1)[0];
      // Title-case the local part so "angela.arce" → "Angela.arce"
      // looks intentional; if there's a dot, take the first segment
      // (most emails use first.last).
      const piece = local.split(".")[0] || local;
      return piece.charAt(0).toUpperCase() + piece.slice(1);
    }
    return "there";
  })();

  // Greeting is computed CLIENT-SIDE off the browser's clock. We
  // used to prefer ``briefing.greeting.headline``, but the server
  // computes that in the ORG'S timezone (or UTC when the org has
  // none configured) — which means "Good morning" at 11pm Eastern
  // because the server thinks it's 3am UTC. The browser is the
  // ground truth on local time, so we ignore the server greeting
  // here and only use the server's ``subline`` for the Mon/Fri
  // narrative when it's present.
  const now = new Date();
  const headline = (() => {
    const h = now.getHours();
    const dow = now.getDay(); // 0 = Sun, 1 = Mon, ..., 5 = Fri
    // Friday after 3pm local — the wind-down greeting, matches the
    // server's special-case copy.
    if (dow === 5 && h >= 15) return `Almost there, ${greetingName}`;
    if (h < 12) return `Good morning, ${greetingName}`;
    if (h < 17) return `Good afternoon, ${greetingName}`;
    return `Good evening, ${greetingName}`;
  })();
  // Subline can still come from the server — it adds context like
  // "Here's your week ahead" on Monday. Falls back to null when
  // missing (which is most of the time).
  const subline = briefing?.greeting.subline || null;
  const today =
    briefing?.today ||
    new Date().toLocaleDateString(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  return (
    <div className="flex flex-col items-center text-center pt-4">
      {/* Centered Lenavio brand mark.
          Asset chain: ``/lenavio-mark.png`` is the canonical 1:1
          dashboard mark; if that 404s (e.g. a Pages deploy that
          shipped the JS but not the public/ asset — yes that
          happened, that's the bug this onError covers) we fall
          through to ``/favicon.svg``, which has been in the repo
          since the very first deploy and is therefore guaranteed
          present on production. We do NOT use ``/logo.png`` as
          a fallback because that's the wordmark, not the square
          mark — the layout reserves a 80x80 square and a wide
          wordmark stretches ugly inside it.
          The two-step onError uses ``currentTarget.onerror = null``
          to prevent an infinite loop if even the favicon 404s. */}
      <img
        src="/lenavio-mark.png"
        alt="Lenavio"
        className="h-20 w-20 mb-3 object-contain"
        draggable={false}
        onError={(e) => {
          const img = e.currentTarget;
          if (img.dataset.fallbackTried === "1") {
            // Already tried the fallback — give up rather than loop.
            img.onerror = null;
            img.style.display = "none";
            return;
          }
          img.dataset.fallbackTried = "1";
          img.src = "/favicon.svg";
        }}
      />
      <h1
        // Syne is the ONLY font we use here — DM Sans / Sora stay
        // for the rest of the app. 28px / 400 / -1px tracking is the
        // exact recipe; Tailwind's tracking utilities top out at
        // ``tracking-tighter`` (≈ -0.05em) which lands looser than
        // the spec at this size, so we keep an inline letterSpacing.
        className="font-greeting text-text-primary mt-5 leading-tight"
        style={{
          fontSize: "28px",
          fontWeight: 400,
          letterSpacing: "-1px",
        }}
      >
        {headline}
      </h1>
      {subline && (
        <p
          className="font-greeting text-text-muted mt-1"
          style={{
            fontSize: "20px",
            fontWeight: 400,
            letterSpacing: "-0.5px",
          }}
        >
          {subline}
        </p>
      )}
      <p className="mt-2 text-text-muted" style={{ fontSize: "13px" }}>
        {today}
      </p>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Section 3 — Module shortcuts
// ════════════════════════════════════════════════════════════════════

const SHORTCUTS: {
  label: string;
  to: string;
  icon: LucideIcon;
  bg: string;
}[] = [
  { label: "People", to: "/employees", icon: Users, bg: "#1B18FF" },
  { label: "Leave", to: "/leave", icon: CalendarDays, bg: "#10B981" },
  { label: "Expenses", to: "/expenses", icon: Wallet, bg: "#F59E0B" },
  { label: "Benefits", to: "/benefits/dashboard", icon: Heart, bg: "#EF4444" },
  { label: "Reports", to: "/reports", icon: FileSearch, bg: "#8B5CF6" },
  { label: "Agents", to: "/agents", icon: Bot, bg: "#F59E0B" },
];

function ModuleShortcuts() {
  return (
    <div className="flex flex-wrap items-center justify-center gap-6">
      {SHORTCUTS.map((s) => {
        const Icon = s.icon;
        return (
          <Link
            key={s.label}
            to={s.to}
            className="group flex flex-col items-center gap-2 transition-transform hover:-translate-y-0.5"
          >
            <span
              className="flex items-center justify-center w-12 h-12 rounded-2xl text-white shadow-md"
              style={{ background: s.bg }}
              aria-hidden="true"
            >
              <Icon className="h-5 w-5" />
            </span>
            <span className="text-xs font-medium text-text-muted group-hover:text-text-primary">
              {s.label}
            </span>
          </Link>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Section 4 — Morning Brief + Next Meeting
// ════════════════════════════════════════════════════════════════════

function MorningBriefCard({
  briefing,
  loading,
}: {
  briefing: DashboardBriefing | null;
  loading: boolean;
}) {
  const pending = briefing?.pending_approvals ?? 0;
  const sentinel = briefing?.sentinel_findings ?? 0;
  const todos = briefing?.todo_count ?? 0;
  const today =
    briefing?.today ||
    new Date().toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });

  return (
    <div
      className="relative overflow-hidden rounded-bento p-6 text-white shadow-bento"
      style={{
        background: "linear-gradient(135deg, #6366F1 0%, #818CF8 100%)",
        minHeight: 220,
      }}
    >
      {/* Decorative blob */}
      <div
        aria-hidden
        className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-20"
        style={{ background: "radial-gradient(#A5B4FC, transparent)" }}
      />
      <div className="relative z-10 flex flex-col h-full">
        <p className="text-[22px] font-medium leading-snug">
          {loading ? (
            "Loading your morning brief…"
          ) : (
            <>
              Today, you've got{" "}
              <strong className="font-bold">{pending}</strong> pending approval
              {pending === 1 ? "" : "s"}. Sentinel found{" "}
              <strong className="font-bold">{sentinel}</strong> compliance
              item{sentinel === 1 ? "" : "s"} this morning. There{" "}
              {todos === 1 ? "is" : "are"}{" "}
              <strong className="font-bold">{todos}</strong> task
              {todos === 1 ? "" : "s"} you could focus on first.
            </>
          )}
        </p>
        <div className="mt-auto pt-6 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 text-xs font-semibold backdrop-blur-sm">
            <Sparkles className="h-3 w-3" /> Morning brief
          </span>
          <span className="text-xs opacity-90">{today}</span>
        </div>
      </div>
    </div>
  );
}

function NextMeetingCard({ meeting }: { meeting: NextMeeting | null }) {
  return (
    <div className="rounded-bento bg-surface border border-border shadow-bento p-6">
      <div className="text-xs font-bold uppercase tracking-[0.1em] text-text-muted">
        Next up
      </div>
      {meeting ? (
        <>
          <h3 className="mt-3 text-lg font-semibold text-text-primary">
            {meeting.title}
          </h3>
          <div className="mt-3 flex items-center gap-3">
            {meeting.attendees.slice(0, 3).map((a, i) => (
              <span
                key={i}
                title={a.name}
                className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[11px] font-bold text-white"
                style={{
                  background: `hsl(${(i * 73) % 360}, 55%, 55%)`,
                }}
                aria-hidden="true"
              >
                {a.initials}
              </span>
            ))}
            <span className="text-sm text-text-muted truncate">
              {meeting.attendees.map((a) => a.name).join(", ")}
            </span>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <span
              className="inline-block w-1 self-stretch rounded-full"
              style={{ background: "#EF4444", minHeight: 24 }}
              aria-hidden="true"
            />
            <div>
              <div className="text-base font-semibold text-text-primary">
                {meeting.time}
              </div>
              {meeting.context && (
                <div className="text-xs text-text-muted">
                  {meeting.context}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="mt-3 text-sm text-text-muted">
          No upcoming meetings on your calendar — you've got a clear
          window.
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Section 5 — To-Do Today + Sentinel Alerts
// ════════════════════════════════════════════════════════════════════

function TodoCard({ todos }: { todos: DashboardTodo[] }) {
  // Local "checked" state — checking an item just crosses it out
  // visually here; mutating the underlying record happens via the
  // link click. This matches every task-list pattern in the app:
  // checkboxes are visual progress, action is the link.
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  return (
    <div
      className="rounded-bento p-5 border border-warning/20"
      style={{ background: "rgba(245,158,11,0.05)" }}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="rounded-md bg-warning/20 p-1.5 text-warning">
          <ListChecks className="h-4 w-4" />
        </div>
        <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-warning">
          To-do today
        </h3>
      </div>
      {todos.length === 0 ? (
        <p className="text-sm text-text-muted">
          Inbox zero — nothing pulling for your attention right now.
        </p>
      ) : (
        <ul className="space-y-2">
          {todos.map((t) => {
            const isChecked = !!checked[t.id];
            const labelClass = `flex-1 text-sm ${
              isChecked
                ? "line-through text-text-muted"
                : "text-text-primary hover:text-warning"
            } ${t.link ? "cursor-pointer" : ""}`;
            return (
              <li key={t.id} className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() =>
                    setChecked((prev) => ({ ...prev, [t.id]: !prev[t.id] }))
                  }
                  className={`flex-shrink-0 w-4 h-4 rounded border-2 transition-colors ${
                    isChecked
                      ? "bg-warning border-warning"
                      : "border-text-muted/40 hover:border-warning"
                  }`}
                  aria-label={
                    isChecked ? `Uncheck ${t.text}` : `Check off ${t.text}`
                  }
                >
                  {isChecked && (
                    <CheckCircle2 className="h-3 w-3 text-white" />
                  )}
                </button>
                {t.link ? (
                  <Link to={t.link} className={labelClass}>
                    {t.text}
                  </Link>
                ) : (
                  <span className={labelClass}>{t.text}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function AlertsCard({ alerts }: { alerts: DashboardAlert[] }) {
  return (
    <div className="rounded-bento p-5 bg-surface border border-border shadow-bento">
      <div className="flex items-center gap-2 mb-3">
        <Bell className="h-4 w-4 text-text-primary" />
        <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-text-primary">
          Sentinel alerts
        </h3>
      </div>
      <ul className="space-y-2.5">
        {alerts.map((a, i) => {
          const dotClass =
            a.severity === "red"
              ? "bg-danger shadow-[0_0_6px_rgba(239,68,68,0.5)]"
              : a.severity === "amber"
                ? "bg-warning"
                : "bg-success";
          const textClass = `text-sm text-text-primary ${
            a.link ? "hover:text-accent cursor-pointer" : ""
          }`;
          return (
            <li key={i} className="flex items-start gap-2.5">
              <span
                aria-hidden="true"
                className={`mt-1.5 inline-block w-2 h-2 rounded-full shrink-0 ${dotClass}`}
              />
              {a.link ? (
                <Link to={a.link} className={textClass}>
                  {a.text}
                </Link>
              ) : (
                <span className={textClass}>{a.text}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Section 6 — Stats bar
// ════════════════════════════════════════════════════════════════════

function StatsBar({ briefing }: { briefing: DashboardBriefing | null }) {
  const stats = briefing?.stats ?? null;
  const items = [
    {
      label: "Total Employees",
      value: stats?.employee_count ?? stats?.total_employees ?? "—",
      delta: stats?.employee_delta ?? "",
      deltaTone: "success" as const,
    },
    {
      label: "Pending",
      value: stats?.pending_count ?? "—",
      delta:
        (stats?.pending_urgent ?? 0) > 0
          ? `${stats?.pending_urgent} urgent`
          : "all clear",
      deltaTone:
        (stats?.pending_urgent ?? 0) > 0
          ? ("danger" as const)
          : ("success" as const),
    },
    {
      label: "Open Roles",
      value: stats?.open_roles ?? "—",
      delta:
        stats?.applicant_count !== undefined
          ? `${stats.applicant_count} applicants`
          : "",
      deltaTone: "success" as const,
    },
    {
      label: "Compliance Score",
      value:
        stats?.compliance_score !== undefined &&
        stats?.compliance_score !== null
          ? `${Math.round(stats.compliance_score * 100)}%`
          : "—",
      delta: stats?.compliance_delta ?? "",
      deltaTone: "success" as const,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {items.map((it, i) => (
        <div
          key={i}
          className="rounded-bento bg-surface-muted/60 p-4 border border-border"
        >
          <div className="text-xs uppercase tracking-[0.08em] text-text-muted font-semibold">
            {it.label}
          </div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-text-primary">
            {it.value}
          </div>
          {it.delta && (
            <div
              className={`mt-1 text-xs font-medium ${
                it.deltaTone === "danger"
                  ? "text-danger"
                  : "text-success"
              }`}
            >
              {it.delta}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Section 7 — Agent cards
// ════════════════════════════════════════════════════════════════════

const AGENT_CONFIG: { name: "atlas" | "sentinel" | "pilot"; label: string; icon: LucideIcon; bg: string }[] = [
  { name: "atlas", label: "Atlas", icon: Briefcase, bg: "#1B18FF" },
  { name: "sentinel", label: "Sentinel", icon: Shield, bg: "#10B981" },
  { name: "pilot", label: "Pilot", icon: Send, bg: "#F59E0B" },
];

function AgentRow({
  agents,
}: {
  agents: { atlas?: AgentSummary; sentinel?: AgentSummary; pilot?: AgentSummary };
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {AGENT_CONFIG.map(({ name, label, icon: Icon, bg }) => {
        const a = agents[name];
        return (
          <Link
            key={name}
            to={`/agents/${name}`}
            className="group rounded-bento bg-surface border border-border shadow-bento p-5 transition-all hover:border-accent/40 hover:-translate-y-0.5"
          >
            <div className="flex items-center gap-3">
              <span
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl text-white shrink-0"
                style={{ background: bg }}
                aria-hidden="true"
              >
                <Icon className="h-5 w-5" />
              </span>
              <div>
                <div className="text-base font-bold text-text-primary">
                  {label}
                </div>
                <div className="text-xs text-text-muted line-clamp-2">
                  {a?.summary || "Not yet run."}
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-xs text-text-muted">
                <span
                  className="inline-block w-2 h-2 rounded-full bg-success animate-pulse"
                  aria-hidden="true"
                />
                {a?.last_run
                  ? `Last run ${formatRelative(a.last_run)}`
                  : "No runs yet"}
              </div>
              <ArrowRight className="h-4 w-4 text-text-muted group-hover:text-accent" />
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function formatRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, (Date.now() - then) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return "recently";
  }
}
